import { useState, useEffect } from "react";
import { ChatCircleIcon, CaretDown } from "@phosphor-icons/react";
import { AgentSession } from "../widget/AgentSession/AgentSession";
import type { UserData } from "../auth";
import type { NextConversation } from "../widget/useCallMachine";
import { IS_IOS } from "../is-ios";

interface AppProps {
  serverUrl: string;
  avatarUrl?: string;
  agentName: string;
  user?: UserData | null;
}


export function App({ serverUrl, avatarUrl, agentName, user }: AppProps) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [panelExpanded, setPanelExpanded] = useState(false);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  // What to open the next session with, set from the ended screen (typed
  // message, quick-start pill, or call button). Cleared on every other remount.
  const [initialMessage, setInitialMessage] = useState<string>();
  const [startCallSignal, setStartCallSignal] = useState(0);
  // Mobile keyboard handling: on phones the panel is full-screen (100dvh), but
  // when the on-screen keyboard opens, position:fixed elements stay anchored to
  // the *layout* viewport (which doesn't shrink) — so the input gets buried
  // behind the keyboard and the header scrolls out of view. We track the
  // *visual* viewport instead and pin the panel to it, keeping the header up top
  // and the input just above the keyboard. Desktop is unaffected.
  const [viewport, setViewport] = useState<{
    height: number;
    offsetTop: number;
    mobile: boolean;
  } | null>(null);

  useEffect(() => {
    if (IS_IOS) return; // see IS_IOS — the pin kills the iOS soft keyboard
    const vv = window.visualViewport;
    const update = () =>
      setViewport({
        height: vv?.height ?? window.innerHeight,
        offsetTop: vv?.offsetTop ?? 0,
        mobile: window.innerWidth <= 768,
      });
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (!avatarUrl) return;
    const expandTimer = setTimeout(() => setExpanded(true), 3000);
    const collapseTimer = setTimeout(() => setExpanded(false), 13000);
    return () => {
      clearTimeout(expandTimer);
      clearTimeout(collapseTimer);
    };
  }, [avatarUrl]);

  const handleOpen = () => {
    setOpen(true);
    setHasOpened(true);
  };

  const handleConversationEnded = (next?: NextConversation) => {
    setSessionEpoch((e) => e + 1);
    setInitialMessage(next?.message);
    setStartCallSignal(next?.voice ? 1 : 0);
  };

  const triggerClass = [
    "lk-widget-trigger",
    open && "lk-widget-trigger--open",
    !open && expanded && avatarUrl && "lk-widget-trigger--expanded",
  ]
    .filter(Boolean)
    .join(" ");

  const panelClass = [
    "lk-widget-panel",
    open && "lk-widget-panel--open",
    panelExpanded && "lk-widget-panel--expanded",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <button
        className={triggerClass}
        onClick={() => (open ? setOpen(false) : handleOpen())}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? (
          <CaretDown size={24} weight="bold" />
        ) : avatarUrl ? (
          <>
            <span className="lk-widget-trigger-avatar">
              <img src={avatarUrl} alt="" />
            </span>
            <span className="lk-widget-trigger-content">
              <span className="lk-widget-trigger-text">
                <span className="lk-widget-trigger-title">
                  Hey, I'm Courtney!
                </span>
                <span className="lk-widget-trigger-subtitle">
                  How can I help you today?
                </span>
              </span>
              <CaretDown className="lk-widget-trigger-chevron" size={10} weight="bold" />
            </span>
          </>
        ) : (
          <ChatCircleIcon size={22} weight="regular" />
        )}
      </button>

      <div
        className={panelClass}
        style={
          viewport?.mobile && open && !IS_IOS
            ? {
                top: `${viewport.offsetTop}px`,
                bottom: "auto",
                height: `${viewport.height}px`,
                maxHeight: `${viewport.height}px`,
              }
            : undefined
        }
      >
        {/* Mounted from page load so the SDK can warm the connection (token +
            prepareConnection). No session starts until hasOpened flips. */}
        {(
          <AgentSession
            key={sessionEpoch}
            opened={hasOpened}
            serverUrl={serverUrl}
            agentName={agentName}
            avatarUrl={avatarUrl}
            user={user}
            onClose={() => setOpen(false)}
            onConversationEnded={handleConversationEnded}
            panelExpanded={panelExpanded}
            onToggleExpand={() => setPanelExpanded((v) => !v)}
            onSetExpanded={setPanelExpanded}
            startCallSignal={startCallSignal}
            initialMessage={initialMessage}
          />
        )}
      </div>
    </>
  );
}
